/*
 * Copyright 2021 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Disable Lint for 3rd party code
/* eslint-disable react/prop-types, react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */

import { Button, Classes, Text } from '@blueprintjs/core';
import React, { useCallback } from 'react';
import styled from 'styled-components';

const Root = styled.div`
  bottom: auto;
  left: auto;
  position: static;
  right: auto;
  top: auto;
  margin-right: 0;
  overflow-y: visible;
  z-index: auto;
  background-color: transparent;
`;

const SimplePanelView = props => {
  const handleClose = useCallback(() => props.onClose(props.panel), [props.onClose, props.panel]);

  const maybeBackButton =
    props.previousPanel === undefined ? null : (
      <Button
        className={Classes.PANEL_STACK_HEADER_BACK}
        icon="chevron-left"
        minimal
        onClick={handleClose}
        small
        text={props.previousPanel.title}
        title={props.previousPanel.htmlTitle}
      />
    );

  return (
    <Root className={Classes.PANEL_STACK2_VIEW}>
      {props.showHeader && (
        <div className={Classes.PANEL_STACK2_HEADER}>
          {/* two <span> tags here ensure title is centered as long as possible, with `flex: 1` styling */}
          <span>{maybeBackButton}</span>
          <Text className={Classes.HEADING} ellipsize title={props.panel.htmlTitle}>
            {props.panel.title}
          </Text>
          <span />
        </div>
      )}
      {/*
       * Cast is required because of error TS2345, where technically `panel.props` could be
       * instantiated with a type unrelated to our generic constraint `T` here. We know
       * we're sending the right values here though, and it makes the consumer API for this
       * component type safe, so it's ok to do this...
       */}
      {props.panel.renderPanel({
        closePanel: handleClose,
        openPanel: props.onOpen,
        ...props.panel.props,
      })}
    </Root>
  );
};

export default SimplePanelView;
